import { ArrowRightToSquare, Smartphone } from "@gravity-ui/icons";
import {
  Modal,
  Flex,
  TextInput,
  Button,
  Icon,
  Text,
  PinInput,
} from "@gravity-ui/uikit";
import { useAction, useAtom } from "@reatom/npm-react";
import { GetCaptchaRequest } from "definitions/poizon-api";
import { useCallback, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Form } from "react-router-dom";

import { createCaptchaPoizonClientStore } from "../../store/fetchers/authPoizonClient";
import css from "./AuthClient.module.scss";

interface NewClientModalProps {
  isOpen: boolean;
  clientId: string;
  onClose: () => void;
}

export interface ISendCaptchaDTO {
  clientId: string;
  getCaptchaRequest: GetCaptchaRequest;
}

export const AuthClientModal = ({
  isOpen,
  onClose,
  clientId,
}: NewClientModalProps) => {
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<ISendCaptchaDTO>({
    mode: "all",
    values: {
      clientId: clientId,
      getCaptchaRequest: { typeId: 0, mobile: "", countryCode: 7 },
    },
  });

  const {
    getCaptchaPoizonClient,
    getCaptchaPoizonClientIsLoading,
    loginPoizonClient,
    loginPoizonClientIsLoading,
  } = useMemo(() => {
    return createCaptchaPoizonClientStore(clientId);
  }, [clientId]);

  const onSubmit: SubmitHandler<ISendCaptchaDTO> = useAction(
    getCaptchaPoizonClient,
  );
  const [codeIsGetted] = useAtom(getCaptchaPoizonClient.dataAtom);
  const codeIsSendedToUser = !!codeIsGetted;

  const [isLoading] = useAtom(getCaptchaPoizonClientIsLoading);

  const loginClient = useAction(loginPoizonClient);
  const [loginIsLoading] = useAtom(loginPoizonClientIsLoading);

  const [codeValue, setCodeValue] = useState("");
  const codeIsValid = useMemo(() => /[0-9]{4}/.test(codeValue), [codeValue]);
  const loginClientHandler = useCallback(
    () =>
      loginClient(
        clientId,
        codeValue,
        getValues("getCaptchaRequest.mobile"),
        getValues("getCaptchaRequest.countryCode"),
      ).then(onClose),
    [clientId, codeValue, getValues, loginClient, onClose],
  );

  return (
    <Modal open={isOpen} onClose={onClose} contentClassName={css.mainContainer}>
      <div className={css.clientText}>
        <Text variant="caption-2" color="hint">
          #{clientId}
        </Text>
      </div>
      <Flex
        className={css.flexContainer}
        alignItems="center"
        justifyContent="center"
        direction="column"
        gap="4"
      >
        <Flex direction="column" gap="0.5" alignItems="center">
          <Text variant="header-1">Авторизация Poizon</Text>
          {codeIsSendedToUser ? (
            <Text>
              Введите SMS/Viber код отправленный на{" "}
              {getValues("getCaptchaRequest.mobile")}
            </Text>
          ) : (
            <Text variant="caption-2">
              Введите номер телефона, вам придет SMS/Viber код
            </Text>
          )}
        </Flex>
        {codeIsSendedToUser ? (
          <Flex direction="column" alignItems="center" gap="3">
            <PinInput
              size="xl"
              placeholder="0"
              validationState={codeIsValid ? undefined : "invalid"}
              onUpdateComplete={(value) => setCodeValue(value.join(""))}
            />
            <Button
              size="xl"
              selected
              view="flat-action"
              loading={loginIsLoading}
              onClick={loginClientHandler}
              disabled={!codeIsValid}
            >
              <Icon data={ArrowRightToSquare} />
              Войти
            </Button>

            <Button
              size="l"
              view="flat-action"
              onClick={handleSubmit(onSubmit)}
            >
              Отправить еще раз
            </Button>
          </Flex>
        ) : (
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Flex direction="column" gap="3" alignItems="center">
              <Flex
                direction="row"
                gap="2"
                justifyContent="center"
                alignItems="center"
                className={css.captchaRow}
              >
                <div className={css.countryCodeContainer}>
                  <TextInput
                    {...register("getCaptchaRequest.countryCode", {
                      required: "*",
                      pattern: /[0-9]+/,
                      setValueAs: (value) => parseInt(value),
                    })}
                    size="xl"
                    label="+"
                    placeholder="7"
                    disabled={codeIsSendedToUser}
                    validationState={
                      errors.getCaptchaRequest?.countryCode
                        ? "invalid"
                        : undefined
                    }
                  />
                </div>
                <div className={css.mobileContainer}>
                  <TextInput
                    {...register("getCaptchaRequest.mobile", {
                      required: "Обязательное поле",
                      pattern: /[0-9]{5,}/,
                    })}
                    size="xl"
                    placeholder="9998884422"
                    disabled={codeIsSendedToUser}
                    validationState={
                      errors.getCaptchaRequest?.mobile ? "invalid" : undefined
                    }
                  />
                </div>
              </Flex>
              <Button
                view="flat-action"
                selected
                size="l"
                type="submit"
                disabled={codeIsSendedToUser}
                loading={isLoading}
              >
                <Icon data={Smartphone} />
                Получить код
              </Button>
            </Flex>
          </Form>
        )}
      </Flex>
    </Modal>
  );
};
